<template>
  <v-container fluid class="analise-page pa-4 pa-md-6">
    <!-- Header -->
    <v-row class="mb-4">
      <v-col cols="12" class="d-flex align-center justify-space-between flex-wrap">
        <div class="d-flex align-center">
          <v-icon color="primary" size="32" class="mr-3">mdi-map-marker-radius</v-icon>
          <div>
            <div class="headline font-weight-bold" style="color: #0E314A">
              Análise de Cobertura de Assessores
            </div>
            <div class="caption grey--text">
              Identifique as áreas com poucos ou nenhum assessor para reforçar a equipe
            </div>
          </div>
        </div>

        <div class="d-flex align-center">
          <!-- Seletor de candidato (apenas para administradores) -->
          <v-select
            v-if="ehAdmin"
            v-model="select_equipe"
            :items="candidatosSelect"
            item-text="nome_completo"
            item-value="idcandidato"
            label="Equipe"
            dense
            hide-details
            outlined
            class="styled-select mr-3"
            style="max-width: 260px"
            @change="carregarDados"
          ></v-select>
          <v-btn color="primary" depressed class="btn-novo" @click="carregarDados">
            <v-icon left small>mdi-refresh</v-icon>
            Atualizar
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <!-- Aviso: nenhum candidato selecionado -->
    <v-alert
      v-if="semCandidato"
      type="info"
      colored-border
      border="left"
      elevation="1"
      class="mb-4"
    >
      Selecione uma equipe (candidato) para visualizar a cobertura dos assessores.
    </v-alert>

    <!-- KPIs -->
    <v-row dense class="mb-2">
      <v-col cols="6" sm="4" md="2">
        <v-card class="kpi-card pa-3" outlined>
          <v-skeleton-loader v-if="loading" type="list-item-two-line"></v-skeleton-loader>
          <div v-else>
            <p class="mb-1 text-uppercase caption grey--text">
              Assessores<dica-metrica :texto="dicaFixa('Assessores')" />
            </p>
            <div class="title font-weight-bold">{{ formatNumber(totalAssessores) }}</div>
          </div>
        </v-card>
      </v-col>
      <v-col cols="6" sm="4" md="2">
        <v-card class="kpi-card pa-3" outlined>
          <v-skeleton-loader v-if="loading" type="list-item-two-line"></v-skeleton-loader>
          <div v-else>
            <p class="mb-1 text-uppercase caption grey--text">
              Municípios cobertos<dica-metrica :texto="dicaFixa('Municípios cobertos')" />
            </p>
            <div class="title font-weight-bold">
              {{ municipiosCobertos }} <span class="caption grey--text">/ {{ coberturaMunicipios.length }}</span>
            </div>
          </div>
        </v-card>
      </v-col>
      <v-col cols="6" sm="4" md="2">
        <v-card class="kpi-card pa-3" outlined>
          <v-skeleton-loader v-if="loading" type="list-item-two-line"></v-skeleton-loader>
          <div v-else>
            <p class="mb-1 text-uppercase caption grey--text">
              Municípios sem assessor<dica-metrica :texto="dicaFixa('Municípios sem assessor')" />
            </p>
            <div class="title font-weight-bold" :class="municipiosSemAssessor > 0 ? 'error--text' : 'success--text'">
              {{ municipiosSemAssessor }}
            </div>
          </div>
        </v-card>
      </v-col>
      <v-col cols="6" sm="4" md="2">
        <v-card class="kpi-card pa-3" outlined>
          <v-skeleton-loader v-if="loading" type="list-item-two-line"></v-skeleton-loader>
          <div v-else>
            <p class="mb-1 text-uppercase caption grey--text">
              Regiões cobertas<dica-metrica :texto="dicaFixa('Regiões cobertas')" />
            </p>
            <div class="title font-weight-bold">
              {{ regioesCobertas }} <span class="caption grey--text">/ {{ zonasGeograficas.length }}</span>
            </div>
          </div>
        </v-card>
      </v-col>
      <v-col cols="6" sm="4" md="2">
        <v-card class="kpi-card pa-3" outlined>
          <v-skeleton-loader v-if="loading" type="list-item-two-line"></v-skeleton-loader>
          <div v-else>
            <p class="mb-1 text-uppercase caption grey--text">
              Regiões sem assessor<dica-metrica :texto="dicaFixa('Regiões sem assessor')" />
            </p>
            <div class="title font-weight-bold" :class="regioesSemAssessor > 0 ? 'error--text' : 'success--text'">
              {{ regioesSemAssessor }}
            </div>
          </div>
        </v-card>
      </v-col>
      <v-col cols="6" sm="4" md="2">
        <v-card class="kpi-card pa-3" outlined>
          <v-skeleton-loader v-if="loading" type="list-item-two-line"></v-skeleton-loader>
          <div v-else>
            <p class="mb-1 text-uppercase caption grey--text">
              Capital / Interior / Ambos<dica-metrica :texto="dicaFixa('Capital / Interior / Ambos')" />
            </p>
            <div class="title font-weight-bold">
              {{ atuacaoCount.capital }} / {{ atuacaoCount.interior }} / {{ atuacaoCount.ambos }}
            </div>
          </div>
        </v-card>
      </v-col>
      <v-col cols="6" sm="4" md="2">
        <v-card class="kpi-card pa-3" outlined>
          <v-skeleton-loader v-if="loading" type="list-item-two-line"></v-skeleton-loader>
          <div v-else>
            <p class="mb-1 text-uppercase caption grey--text">
              Pessoas cadastradas<dica-metrica :texto="dicaFixa('Pessoas cadastradas')" />
            </p>
            <div class="title font-weight-bold">{{ formatNumber(totalPessoas) }}</div>
          </div>
        </v-card>
      </v-col>
      <v-col cols="6" sm="4" md="2">
        <v-card class="kpi-card pa-3" outlined>
          <v-skeleton-loader v-if="loading" type="list-item-two-line"></v-skeleton-loader>
          <div v-else>
            <p class="mb-1 text-uppercase caption grey--text">
              Pessoas em zona sem assessor<dica-metrica :texto="dicaFixa('Pessoas em zona sem assessor')" />
            </p>
            <div class="title font-weight-bold" :class="pessoasEmZonaSemAssessor > 0 ? 'error--text' : 'success--text'">
              {{ formatNumber(pessoasEmZonaSemAssessor) }}
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- KPIs de resultado (boletim de urna importado) -->
    <v-row v-if="temApuracao && !semCandidato" dense class="mb-2">
      <v-col v-for="k in kpisResultado" :key="k.titulo" cols="6" sm="4" md="2">
        <v-card class="kpi-card pa-3" outlined>
          <v-skeleton-loader v-if="loading" type="list-item-two-line"></v-skeleton-loader>
          <div v-else>
            <p class="mb-1 text-uppercase caption grey--text">
              {{ k.titulo }}<dica-metrica :texto="k.dica" />
            </p>
            <div class="title font-weight-bold" :class="k.cor">{{ k.valor }}</div>
            <div class="caption grey--text">{{ k.legenda }}</div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Abas Interior / Capital -->
    <v-card outlined class="table-card">
      <v-tabs v-model="tab" background-color="transparent" color="primary" class="px-2">
        <v-tab>
          <v-icon left small>mdi-pine-tree</v-icon>
          Interior (Municípios)
        </v-tab>
        <v-tab>
          <v-icon left small>mdi-city-variant-outline</v-icon>
          Capital (Regiões)
        </v-tab>
        <v-tab>
          <v-icon left small>mdi-vote-outline</v-icon>
          Por Zona Eleitoral
        </v-tab>
        <v-tab v-if="temApuracao">
          <v-icon left small>mdi-chart-scatter-plot</v-icon>
          Cadastro × Voto
        </v-tab>
      </v-tabs>
      <v-divider></v-divider>

      <v-tabs-items v-model="tab">
        <!-- ============ INTERIOR ============ -->
        <v-tab-item>
          <v-card-text v-if="!loading && municipiosCobertosLista.length">
            <div class="subtitle-2 grey--text text--darken-1 mb-2">
              Assessores por município (somente com cobertura)
            </div>
            <apexchart
              type="bar"
              :height="alturaGraficoInterior"
              :options="opcoesGraficoInterior"
              :series="seriesGraficoInterior"
            ></apexchart>
          </v-card-text>

          <v-divider></v-divider>

          <div class="d-flex align-center flex-wrap px-4 pt-3">
            <v-text-field
              v-model="searchInterior"
              append-icon="mdi-magnify"
              label="Buscar município"
              single-line
              hide-details
              dense
              outlined
              class="styled-input"
              style="max-width: 320px"
            ></v-text-field>
            <v-spacer></v-spacer>
            <v-switch
              v-model="soDeficitInterior"
              label="Somente sem cobertura"
              hide-details
              dense
              class="mt-0"
            ></v-switch>
          </div>

          <v-data-table
            :headers="comResultado(headersMunicipio)"
            :items="municipiosFiltrados"
            :search="searchInterior"
            :loading="loading"
            loading-text="Carregando cobertura..."
            :items-per-page="15"
            class="modern-table clickable-rows"
            dense
            @click:row="abrirArea"
          >
          <template v-for="h in comDica(comResultado(headersMunicipio))" v-slot:[`header.${h.value}`]="{ header }">
            <span :key="h.value">{{ header.text }}<dica-metrica :texto="header.dica" /></span>
          </template>
            <template v-slot:item.pessoas="{ item }">
              {{ formatNumber(item.pessoas) }}
            </template>
            <template v-slot:item.eleitorado="{ item }">
              <span v-if="item.eleitorado > 0">{{ formatNumber(item.eleitorado) }}</span>
              <span v-else class="grey--text">—</span>
            </template>
            <template v-slot:item.penetracao="{ item }">
              <span v-if="item.penetracao !== null">{{ item.penetracao.toFixed(2) }}%</span>
              <span v-else class="grey--text">—</span>
            </template>
            <template v-slot:item.total="{ item }">
              <v-chip small dark :color="item.total > 0 ? corCobertura(item.total) : 'error'">
                {{ item.total }}
              </v-chip>
            </template>
            <template v-slot:item.votos="{ item }">{{ formatNumber(item.votos) }}</template>
            <template v-slot:item.conversao="{ item }">
              <span v-if="item.conversao !== null">{{ formatPct(item.conversao) }}</span>
              <span v-else class="grey--text">—</span>
            </template>
            <template v-slot:item.aproveitamento="{ item }">
              <v-tooltip v-if="item.aproveitamento !== null" bottom>
                <template v-slot:activator="{ on }">
                  <v-chip x-small dark :color="corAproveitamento(item.aproveitamento)" v-on="on">
                    {{ formatPct(item.aproveitamento) }}
                  </v-chip>
                </template>
                Pontuação {{ formatDecimal(item.pontuacao) }} de {{ formatNumber(item.pessoas) }} cadastros
              </v-tooltip>
              <span v-else class="grey--text">—</span>
            </template>
            <template v-slot:no-data>
              <div class="text-center pa-8 grey--text">Nenhum município encontrado.</div>
            </template>
          </v-data-table>
        </v-tab-item>

        <!-- ============ CAPITAL ============ -->
        <v-tab-item>
          <v-card-text v-if="!loading && regioesComCobertura > 0">
            <v-row>
              <v-col cols="12" md="6">
                <div class="subtitle-2 grey--text text--darken-1 mb-2">
                  Distribuição por região da capital
                </div>
                <apexchart
                  type="donut"
                  height="320"
                  :options="opcoesGraficoCapital"
                  :series="seriesGraficoCapital"
                ></apexchart>
              </v-col>
              <v-col cols="12" md="6" class="d-flex align-center">
                <v-alert
                  v-if="regioesSemAssessor > 0"
                  type="warning"
                  colored-border
                  border="left"
                  elevation="0"
                  class="mb-0"
                  style="width: 100%"
                >
                  <span class="font-weight-bold">{{ regioesSemAssessor }}</span> região(ões) da capital
                  sem nenhum assessor atuando. Considere reforçar a cobertura nessas áreas.
                </v-alert>
                <v-alert v-else type="success" colored-border border="left" elevation="0" class="mb-0" style="width: 100%">
                  Todas as regiões da capital possuem ao menos um assessor.
                </v-alert>
              </v-col>
            </v-row>
          </v-card-text>

          <v-divider></v-divider>

          <div class="d-flex align-center flex-wrap px-4 pt-3">
            <v-spacer></v-spacer>
            <v-switch
              v-model="soDeficitCapital"
              label="Somente sem cobertura"
              hide-details
              dense
              class="mt-0"
            ></v-switch>
          </div>

          <v-data-table
            :headers="comResultado(headersRegiao)"
            :items="regioesFiltradas"
            :loading="loading"
            loading-text="Carregando cobertura..."
            :items-per-page="10"
            class="modern-table clickable-rows"
            dense
            @click:row="abrirRegiao"
          >
          <template v-for="h in comDica(comResultado(headersRegiao))" v-slot:[`header.${h.value}`]="{ header }">
            <span :key="h.value">{{ header.text }}<dica-metrica :texto="header.dica" /></span>
          </template>
            <template v-slot:item.pessoas="{ item }">
              {{ formatNumber(item.pessoas) }}
            </template>
            <template v-slot:item.eleitorado="{ item }">
              <span v-if="item.eleitorado > 0">{{ formatNumber(item.eleitorado) }}</span>
              <span v-else class="grey--text">—</span>
            </template>
            <template v-slot:item.penetracao="{ item }">
              <span v-if="item.penetracao !== null">{{ item.penetracao.toFixed(2) }}%</span>
              <span v-else class="grey--text">—</span>
            </template>
            <template v-slot:item.total="{ item }">
              <v-chip small dark :color="item.total > 0 ? corCobertura(item.total) : 'error'">
                {{ item.total }}
              </v-chip>
            </template>
            <template v-slot:item.votos="{ item }">{{ formatNumber(item.votos) }}</template>
            <template v-slot:item.conversao="{ item }">
              <span v-if="item.conversao !== null">{{ formatPct(item.conversao) }}</span>
              <span v-else class="grey--text">—</span>
            </template>
            <template v-slot:item.aproveitamento="{ item }">
              <v-tooltip v-if="item.aproveitamento !== null" bottom>
                <template v-slot:activator="{ on }">
                  <v-chip x-small dark :color="corAproveitamento(item.aproveitamento)" v-on="on">
                    {{ formatPct(item.aproveitamento) }}
                  </v-chip>
                </template>
                Pontuação {{ formatDecimal(item.pontuacao) }} de {{ formatNumber(item.pessoas) }} cadastros
              </v-tooltip>
              <span v-else class="grey--text">—</span>
            </template>
            <template v-slot:no-data>
              <div class="text-center pa-8 grey--text">Nenhuma região encontrada.</div>
            </template>
          </v-data-table>
        </v-tab-item>

        <!-- ============ POR ZONA ELEITORAL ============ -->
        <v-tab-item>
          <v-card-text>
            <v-alert
              v-if="zonasNaoQualificadas.length"
              type="warning"
              colored-border
              border="left"
              elevation="0"
              dense
              class="mb-0"
            >
              Há pessoas cadastradas em {{ zonasNaoQualificadas.length }} zona(s) ainda não
              cadastrada(s) em <strong>Cadastros → Zonas</strong>
              (nº {{ zonasNaoQualificadas.join(", ") }}) — elas não entram no cruzamento abaixo.
            </v-alert>
            <div v-else class="caption grey--text">
              Cruzamento entre pessoas cadastradas (por zona eleitoral) e os assessores que
              atuam nos municípios/região de cada zona. Zonas com pessoas e sem assessor
              aparecem no topo.
            </div>
          </v-card-text>

          <v-divider></v-divider>

          <div class="d-flex align-center flex-wrap px-4 pt-3">
            <v-text-field
              v-model="searchZona"
              append-icon="mdi-magnify"
              label="Buscar zona ou município"
              single-line
              hide-details
              dense
              outlined
              class="styled-input"
              style="max-width: 340px"
            ></v-text-field>
            <v-spacer></v-spacer>
            <v-switch
              v-model="soDeficitZona"
              label="Somente sem assessor"
              hide-details
              dense
              class="mt-0"
            ></v-switch>
          </div>

          <v-data-table
            :headers="comResultado(headersZona)"
            :items="zonasFiltradas"
            :search="searchZona"
            :loading="loading"
            loading-text="Cruzando dados..."
            :items-per-page="15"
            class="modern-table clickable-rows"
            dense
            @click:row="abrirZona"
          >
          <template v-for="h in comDica(comResultado(headersZona))" v-slot:[`header.${h.value}`]="{ header }">
            <span :key="h.value">{{ header.text }}<dica-metrica :texto="header.dica" /></span>
          </template>
            <template v-slot:item.zona="{ item }">
              <span class="font-weight-bold">Zona {{ item.zona }}</span>
            </template>
            <template v-slot:item.municipiosNomes="{ item }">
              <span class="caption">{{ item.municipiosNomes || "—" }}</span>
              <v-chip v-if="item.regiao" x-small color="primary" outlined class="ml-1">{{ item.regiao }}</v-chip>
            </template>
            <template v-slot:item.pessoas="{ item }">
              {{ formatNumber(item.pessoas) }}
            </template>
            <template v-slot:item.eleitorado="{ item }">
              <span v-if="item.eleitorado > 0">{{ formatNumber(item.eleitorado) }}</span>
              <span v-else class="grey--text">—</span>
            </template>
            <template v-slot:item.penetracao="{ item }">
              <span v-if="item.penetracao !== null">{{ item.penetracao.toFixed(1) }}%</span>
              <span v-else class="grey--text">—</span>
            </template>
            <template v-slot:item.total="{ item }">
              <v-chip small dark :color="item.total > 0 ? corCobertura(item.total) : 'error'">
                {{ item.total }}
              </v-chip>
            </template>
            <template v-slot:item.votos="{ item }">{{ formatNumber(item.votos) }}</template>
            <template v-slot:item.conversao="{ item }">
              <span v-if="item.conversao !== null">{{ formatPct(item.conversao) }}</span>
              <span v-else class="grey--text">—</span>
            </template>
            <template v-slot:item.aproveitamento="{ item }">
              <v-tooltip v-if="item.aproveitamento !== null" bottom>
                <template v-slot:activator="{ on }">
                  <v-chip x-small dark :color="corAproveitamento(item.aproveitamento)" v-on="on">
                    {{ formatPct(item.aproveitamento) }}
                  </v-chip>
                </template>
                Pontuação {{ formatDecimal(item.pontuacao) }} de {{ formatNumber(item.pessoas) }} cadastros
              </v-tooltip>
              <span v-else class="grey--text">—</span>
            </template>
            <template v-slot:no-data>
              <div class="text-center pa-8 grey--text">Nenhuma zona encontrada.</div>
            </template>
          </v-data-table>
        </v-tab-item>

        <!-- ============ CADASTRO x VOTO (quadrante) ============ -->
        <v-tab-item v-if="temApuracao">
          <v-card-text>
            <div class="d-flex align-center flex-wrap mb-2">
              <div class="caption grey--text" style="max-width: 720px">
                Cada ponto é uma área: no eixo X a penetração do cadastro (cadastros ÷ eleitorado),
                no eixo Y a participação no voto (votos ÷ eleitorado). As linhas tracejadas são as
                medianas e dividem as áreas em quatro grupos. Clique num ponto para detalhar.
              </div>
              <v-spacer></v-spacer>
              <v-btn-toggle v-model="quadranteNivel" mandatory dense color="primary">
                <v-btn small value="zona">Zonas</v-btn>
                <v-btn small value="municipio">Municípios (interior)</v-btn>
              </v-btn-toggle>
            </div>
            <apexchart
              v-if="!loading && quadrante.pontos.length"
              type="scatter"
              height="440"
              :options="opcoesQuadrante"
              :series="seriesQuadrante"
            ></apexchart>
            <div v-else-if="!loading" class="text-center pa-8 grey--text">
              Sem áreas com eleitorado para comparar.
            </div>

            <v-row class="mt-2">
              <v-col v-for="g in gruposQuadrante" :key="g.chave" cols="12" md="6">
                <div class="d-flex align-center mb-1">
                  <v-icon small :color="g.cor" class="mr-1">mdi-circle</v-icon>
                  <span class="subtitle-2 font-weight-bold">{{ g.titulo }}</span>
                  <span class="caption grey--text ml-2">{{ g.pontos.length }} área(s)</span>
                </div>
                <div class="caption grey--text mb-1">{{ g.descricao }}</div>
                <v-simple-table dense>
                  <tbody>
                    <tr
                      v-for="p in g.destaques"
                      :key="g.chave + p.nome"
                      class="clickable-row"
                      @click="abrirPontoQuadrante(p)"
                    >
                      <td>{{ p.nome }}</td>
                      <td class="text-right caption">{{ formatNumber(p.pessoas) }} cad.</td>
                      <td class="text-right caption">{{ formatNumber(p.votos) }} votos</td>
                      <td class="text-right">
                        <v-chip x-small dark :color="corAproveitamento(p.aproveitamento)">
                          {{ p.aproveitamento !== null ? formatPct(p.aproveitamento) : "—" }}
                        </v-chip>
                      </td>
                    </tr>
                    <tr v-if="!g.destaques.length">
                      <td class="caption grey--text">Nenhuma área neste grupo.</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </v-col>
            </v-row>
          </v-card-text>
        </v-tab-item>
      </v-tabs-items>
    </v-card>

    <!-- Modal de drill-down: assessores da área -->
    <v-dialog v-model="dialogArea" max-width="560">
      <v-card class="dialog-card">
        <v-card-title class="pa-4">
          <v-icon color="primary" class="mr-2">mdi-account-group</v-icon>
          <span class="subtitle-1 font-weight-bold">{{ areaSelecionada.nome }}</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="dialogArea = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          <div v-if="areaSelecionada.assessores.length">
            <div class="d-flex justify-space-between align-center caption grey--text mb-1 px-1">
              <span>
                Meta e realizado do assessor no candidato<span v-if="temApuracao">; pontuação total
                  dele no candidato, não só nesta área</span>
              </span>
              <span class="font-weight-bold">
                Σ {{ formatNumber(resumoAreaAssessores.realizado) }} / {{ formatNumber(resumoAreaAssessores.meta) }}
              </span>
            </div>
            <v-list dense>
              <v-list-item v-for="a in areaSelecionada.assessores" :key="a.id">
                <v-list-item-avatar>
                  <v-avatar color="primary" size="36">
                    <span class="white--text">{{ iniciais(a.name) }}</span>
                  </v-avatar>
                </v-list-item-avatar>
                <v-list-item-content>
                  <div class="d-flex align-center justify-space-between">
                    <div>
                      <v-list-item-title>{{ a.name }}</v-list-item-title>
                      <v-list-item-subtitle class="caption">
                        {{ atuacaoLabel(a.tipo_atuacao) }}<span v-if="a.setor"> · {{ a.setor }}</span>
                      </v-list-item-subtitle>
                    </div>
                    <div class="text-right ml-2">
                      <div class="subtitle-2 font-weight-bold">
                        {{ formatNumber(a.realizado) }}
                        <span class="grey--text font-weight-regular">/ {{ a.meta ? formatNumber(a.meta) : "—" }}</span>
                      </div>
                      <v-chip x-small dark :color="corMeta(a.percentual)">
                        {{ a.percentual !== null ? a.percentual.toFixed(0) + "%" : "s/ meta" }}
                      </v-chip>
                      <div v-if="temApuracao" class="caption">
                        pontuação <strong>{{ formatDecimal(a.pontuacao) }}</strong>
                        <v-chip x-small dark class="ml-1" :color="corAproveitamento(aproveitamentoAssessor(a))">
                          {{ aproveitamentoAssessor(a) !== null ? formatPct(aproveitamentoAssessor(a)) : "—" }}
                        </v-chip>
                      </div>
                    </div>
                  </div>
                  <v-progress-linear
                    rounded
                    height="6"
                    class="mt-1"
                    :value="a.percentual !== null ? Math.min(a.percentual, 100) : 0"
                    :color="corMeta(a.percentual)"
                  ></v-progress-linear>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </div>
          <div v-else class="text-center pa-6 grey--text">
            <v-icon size="48" color="error" class="mb-2">mdi-map-marker-off</v-icon>
            <div class="subtitle-2 error--text font-weight-bold">Área descoberta</div>
            <div class="caption">Nenhum assessor atua nesta área para o candidato selecionado.</div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Modal de drill-down por região: bairros + assessores -->
    <v-dialog v-model="dialogRegiao" :max-width="temApuracao ? 860 : 640">
      <v-card class="dialog-card">
        <v-card-title class="pa-4">
          <v-icon color="primary" class="mr-2">mdi-city-variant-outline</v-icon>
          <span class="subtitle-1 font-weight-bold">{{ regiaoSelecionada.titulo }}</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="dialogRegiao = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          <v-row dense class="mb-3">
            <v-col cols="4" class="text-center">
              <div class="caption grey--text text-uppercase">Pessoas</div>
              <div class="title font-weight-bold">{{ formatNumber(regiaoSelecionada.pessoas) }}</div>
            </v-col>
            <v-col cols="4" class="text-center">
              <div class="caption grey--text text-uppercase">Eleitorado</div>
              <div class="title font-weight-bold">
                <span v-if="regiaoSelecionada.eleitorado > 0">{{ formatNumber(regiaoSelecionada.eleitorado) }}</span>
                <span v-else class="grey--text">—</span>
              </div>
            </v-col>
            <v-col cols="4" class="text-center">
              <div class="caption grey--text text-uppercase">Penetração</div>
              <div class="title font-weight-bold">
                <span v-if="regiaoSelecionada.penetracao != null">{{ regiaoSelecionada.penetracao.toFixed(2) }}%</span>
                <span v-else class="grey--text">—</span>
              </div>
            </v-col>
            <template v-if="temApuracao">
              <v-col cols="4" class="text-center">
                <div class="caption grey--text text-uppercase">Votos</div>
                <div class="title font-weight-bold">{{ formatNumber(regiaoSelecionada.votos) }}</div>
              </v-col>
              <v-col cols="4" class="text-center">
                <div class="caption grey--text text-uppercase">Conversão</div>
                <div class="title font-weight-bold">
                  <span v-if="regiaoSelecionada.conversao != null">{{ formatPct(regiaoSelecionada.conversao) }}</span>
                  <span v-else class="grey--text">—</span>
                </div>
              </v-col>
              <v-col cols="4" class="text-center">
                <div class="caption grey--text text-uppercase">Aproveitamento</div>
                <div class="title font-weight-bold">
                  <span v-if="regiaoSelecionada.aproveitamento != null">{{ formatPct(regiaoSelecionada.aproveitamento) }}</span>
                  <span v-else class="grey--text">—</span>
                </div>
              </v-col>
            </template>
          </v-row>
          <v-divider class="mb-3"></v-divider>
          <div class="subtitle-2 grey--text text--darken-1 mb-2">Pessoas por bairro</div>
          <v-data-table
            :headers="comResultado(headersBairro)"
            :items="regiaoSelecionada.bairros"
            :items-per-page="8"
            dense
            class="modern-table"
          >
          <template v-for="h in comDica(comResultado(headersBairro))" v-slot:[`header.${h.value}`]="{ header }">
            <span :key="h.value">{{ header.text }}<dica-metrica :texto="header.dica" /></span>
          </template>
            <template v-slot:item.pessoas="{ item }">{{ formatNumber(item.pessoas) }}</template>
            <template v-slot:item.eleitorado="{ item }">
              <span v-if="item.eleitorado > 0">{{ formatNumber(item.eleitorado) }}</span>
              <span v-else class="grey--text">—</span>
            </template>
            <template v-slot:item.penetracao="{ item }">
              <span v-if="item.penetracao !== null">{{ item.penetracao.toFixed(2) }}%</span>
              <span v-else class="grey--text">—</span>
            </template>
            <template v-slot:item.votos="{ item }">{{ formatNumber(item.votos) }}</template>
            <template v-slot:item.conversao="{ item }">
              <span v-if="item.conversao !== null">{{ formatPct(item.conversao) }}</span>
              <span v-else class="grey--text">—</span>
            </template>
            <template v-slot:item.aproveitamento="{ item }">
              <v-tooltip v-if="item.aproveitamento !== null" bottom>
                <template v-slot:activator="{ on }">
                  <v-chip x-small dark :color="corAproveitamento(item.aproveitamento)" v-on="on">
                    {{ formatPct(item.aproveitamento) }}
                  </v-chip>
                </template>
                Pontuação {{ formatDecimal(item.pontuacao) }} de {{ formatNumber(item.pessoas) }} cadastros
              </v-tooltip>
              <span v-else class="grey--text">—</span>
            </template>
            <template v-slot:no-data>
              <div class="text-center pa-4 grey--text">Sem bairros para esta região.</div>
            </template>
          </v-data-table>

          <div class="subtitle-2 grey--text text--darken-1 mt-4 mb-1">
            Assessores atuando na área
            <span class="caption">({{ regiaoSelecionada.assessores.length }})</span>
            <span v-if="temApuracao" class="caption grey--text">
              — pontuação total do assessor no candidato, não só nesta área
            </span>
          </div>
          <v-list v-if="regiaoSelecionada.assessores.length" dense>
            <v-list-item v-for="a in regiaoSelecionada.assessores" :key="a.id">
              <v-list-item-avatar>
                <v-avatar color="primary" size="34">
                  <span class="white--text caption">{{ iniciais(a.name) }}</span>
                </v-avatar>
              </v-list-item-avatar>
              <v-list-item-content>
                <div class="d-flex align-center justify-space-between">
                  <v-list-item-title>{{ a.name }}</v-list-item-title>
                  <div class="text-right ml-2 d-flex align-center">
                    <span class="subtitle-2 font-weight-bold mr-2">
                      {{ formatNumber(a.realizado) }}
                      <span class="grey--text font-weight-regular">/ {{ a.meta ? formatNumber(a.meta) : "—" }}</span>
                    </span>
                    <v-chip x-small dark :color="corMeta(a.percentual)">
                      {{ a.percentual !== null ? a.percentual.toFixed(0) + "%" : "s/ meta" }}
                    </v-chip>
                  </div>
                </div>
                <div v-if="temApuracao" class="caption text-right">
                  pontuação <strong>{{ formatDecimal(a.pontuacao) }}</strong>
                  <v-chip x-small dark class="ml-1" :color="corAproveitamento(aproveitamentoAssessor(a))">
                    {{ aproveitamentoAssessor(a) !== null ? formatPct(aproveitamentoAssessor(a)) : "—" }}
                  </v-chip>
                </div>
                <v-progress-linear
                  rounded
                  height="5"
                  class="mt-1"
                  :value="a.percentual !== null ? Math.min(a.percentual, 100) : 0"
                  :color="corMeta(a.percentual)"
                ></v-progress-linear>
              </v-list-item-content>
            </v-list-item>
          </v-list>
          <div v-else class="caption error--text font-weight-bold">
            Nenhum assessor atua nesta área para o candidato selecionado.
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import Vue from "vue";
import VueApexCharts from "vue-apexcharts";
import UsuarioAPI from "./../../api/usuario";
import AclAPI from "./../../api/acl";
import DashboardAPI from "./../../api/dashboard";
import DicaMetrica from "./DicaMetrica.vue";
import { DESCRICOES } from "./descricoesMetricas";

Vue.component("apexchart", VueApexCharts);

/** Explicação dos KPIs fixos (cobertura de assessores). */
const DICAS_KPI = {
  "Assessores": "Assessores vinculados ao candidato selecionado.",
  "Municípios cobertos": "Municípios do interior com pelo menos um assessor que declara atuar neles.",
  "Municípios sem assessor": "Municípios do interior sem nenhum assessor atuando: áreas descobertas.",
  "Regiões cobertas": "Regiões da capital com pelo menos um assessor que declara atuar nelas.",
  "Regiões sem assessor": "Regiões da capital sem nenhum assessor atuando: áreas descobertas.",
  "Capital / Interior / Ambos": "Quantos assessores atuam só na capital, só no interior ou nos dois.",
  "Pessoas cadastradas": DESCRICOES.cadastros,
  "Pessoas em zona sem assessor": "Cadastros em zonas eleitorais onde nenhum assessor declara atuação.",
};

/** Mesmo id usado no backend (AclController::getBairros, MapaCalorController). */
const ID_MANAUS = 38;

export default {
  name: "AnaliseCoberturaAssessores",
  components: { DicaMetrica },
  data() {
    return {
      loading: false,
      tab: 0,
      ehAdmin: false,
      select_equipe: "null",
      candidatos: [],
      assessores: [],
      municipios: [],
      zonasGeograficas: [],
      zonas: [],
      pessoas: [],
      votos: [],
      temApuracao: false,
      quadranteNivel: "zona",
      bairros: [],
      zonaBairros: [],
      dialogRegiao: false,
      regiaoSelecionada: { titulo: "", nome: "", pessoas: 0, eleitorado: 0, penetracao: null, bairros: [], assessores: [] },
      searchInterior: "",
      searchZona: "",
      soDeficitInterior: false,
      soDeficitCapital: false,
      soDeficitZona: false,
      dialogArea: false,
      areaSelecionada: { nome: "", assessores: [] },
      headersArea: [
        { text: "Área", value: "nome", align: "start" },
        { text: "Nº assessores", value: "total", align: "center", width: 140 },
        { text: "", value: "acao", align: "end", sortable: false, width: 120 },
      ],
      headersBairro: [
        { text: "Bairro", value: "nome", align: "start" },
        { text: "Pessoas", value: "pessoas", align: "center", width: 100, dica: DESCRICOES.cadastros },
        { text: "Eleitorado", value: "eleitorado", align: "center", width: 110, dica: DESCRICOES.eleitorado },
        { text: "Penetração", value: "penetracao", align: "center", width: 110, dica: DESCRICOES.penetracao },
      ],
      headersMunicipio: [
        { text: "Município", value: "nome", align: "start" },
        { text: "Pessoas", value: "pessoas", align: "center", width: 100, dica: DESCRICOES.cadastros },
        { text: "Eleitorado", value: "eleitorado", align: "center", width: 110, dica: DESCRICOES.eleitorado },
        { text: "Penetração", value: "penetracao", align: "center", width: 110, dica: DESCRICOES.penetracao },
        { text: "Assessores", value: "total", align: "center", width: 110, dica: DESCRICOES.assessoresCobertura },
      ],
      headersRegiao: [
        { text: "Região", value: "nome", align: "start" },
        { text: "Pessoas", value: "pessoas", align: "center", width: 110, dica: DESCRICOES.cadastros },
        { text: "Eleitorado", value: "eleitorado", align: "center", width: 120, dica: DESCRICOES.eleitorado },
        { text: "Penetração", value: "penetracao", align: "center", width: 120, dica: DESCRICOES.penetracao },
        { text: "Assessores", value: "total", align: "center", width: 120, dica: DESCRICOES.assessoresCobertura },
      ],
      headersZona: [
        { text: "Zona", value: "zona", align: "start", width: 90 },
        { text: "Municípios / Região", value: "municipiosNomes", align: "start" },
        { text: "Pessoas", value: "pessoas", align: "center", width: 110, dica: DESCRICOES.cadastros },
        { text: "Eleitorado", value: "eleitorado", align: "center", width: 120, dica: DESCRICOES.eleitorado },
        { text: "Penetração", value: "penetracao", align: "center", width: 120, dica: DESCRICOES.penetracao },
        { text: "Assessores", value: "total", align: "center", width: 120, dica: DESCRICOES.assessoresCobertura },
      ],
    };
  },
  computed: {
    semCandidato() {
      return !this.select_equipe || this.select_equipe === "null";
    },
    candidatosSelect() {
      return this.candidatos;
    },
    totalAssessores() {
      return this.assessores.length;
    },
    resumoAreaAssessores() {
      const lista = this.areaSelecionada.assessores || [];
      return {
        realizado: lista.reduce((s, a) => s + (a.realizado || 0), 0),
        meta: lista.reduce((s, a) => s + (a.meta || 0), 0),
      };
    },
    atuacaoCount() {
      const c = { capital: 0, interior: 0, ambos: 0 };
      this.assessores.forEach((a) => {
        if (a.tipo_atuacao && c[a.tipo_atuacao] !== undefined) c[a.tipo_atuacao] += 1;
      });
      return c;
    },
    // Pessoas por município (via seção → id_municipio; preciso)
    pessoasPorMunicipio() {
      const mapa = {};
      this.pessoas.forEach((p) => {
        const id = p.id_municipio;
        if (id === null || id === undefined || id === "") return;
        mapa[id] = (mapa[id] || 0) + Number(p.total);
      });
      return mapa;
    },
    // Cobertura por município (interior; exclui Manaus, que é capital → aba Capital)
    coberturaMunicipios() {
      return this.municipios
        .filter((m) => m.nome !== "Manaus")
        .map((m) => {
          const assessores = this.assessores.filter(
            (a) => Array.isArray(a.municipios) && a.municipios.includes(m.id)
          );
          const pessoas = this.pessoasPorMunicipio[m.id] || 0;
          const eleitorado = Number(m.eleitorado) || 0;
          return {
            id: m.id,
            nome: m.nome,
            total: assessores.length,
            assessores,
            pessoas,
            eleitorado,
            penetracao: eleitorado > 0 ? (pessoas / eleitorado) * 100 : null,
            ...this.resultado("municipio", m.id, pessoas, eleitorado),
          };
        })
        .sort((a, b) => a.total - b.total || a.nome.localeCompare(b.nome));
    },
    // Pessoas por região da capital (via seção → bairro → região; preciso)
    pessoasPorRegiao() {
      const mapa = {};
      this.pessoas.forEach((p) => {
        const r = p.id_zona_geografica;
        if (r === null || r === undefined || r === "") return;
        mapa[r] = (mapa[r] || 0) + Number(p.total);
      });
      return mapa;
    },
    // Pessoas por bairro da capital (via seção). Só Manaus: há 28 nomes de bairro
    // repetidos em municípios do interior (ex.: CIDADE NOVA em Fonte Boa).
    pessoasPorBairro() {
      const mapa = {};
      this.pessoas.forEach((p) => {
        if (!p.nm_bairro || Number(p.id_municipio) !== ID_MANAUS) return;
        mapa[p.nm_bairro] = (mapa[p.nm_bairro] || 0) + Number(p.total);
      });
      return mapa;
    },
    // Pessoas por "zona|bairro" (para o drill-down por bairro da zona)
    pessoasPorZonaBairro() {
      const mapa = {};
      this.pessoas.forEach((p) => {
        if (!p.nm_bairro || p.zona === null || p.zona === undefined || p.zona === "") return;
        const k = Number(p.zona) + "|" + p.nm_bairro;
        mapa[k] = (mapa[k] || 0) + Number(p.total);
      });
      return mapa;
    },
    // Cobertura por região da capital + pessoas/eleitorado/penetração
    coberturaRegioes() {
      return this.zonasGeograficas
        .map((z) => {
          const assessores = this.assessores.filter(
            (a) => Array.isArray(a.zonas_geograficas) && a.zonas_geograficas.includes(z.id)
          );
          const pessoas = this.pessoasPorRegiao[z.id] || 0;
          const eleitorado = Number(z.eleitorado) || 0;
          return {
            id: z.id,
            nome: z.nome,
            total: assessores.length,
            assessores,
            pessoas,
            eleitorado,
            penetracao: eleitorado > 0 ? (pessoas / eleitorado) * 100 : null,
            ...this.resultado("regiao", z.id, pessoas, eleitorado),
          };
        })
        .sort((a, b) => a.total - b.total || a.nome.localeCompare(b.nome));
    },
    // ---- Resultado da apuração (boletim) por área ----
    // Mapas { chave: valor } de pontuação (Σ fator dos cadastros) e votos, nas
    // mesmas chaves dos pessoasPor*.
    mapasResultado() {
      const chaves = {
        municipio: (r) => r.id_municipio,
        regiao: (r) => r.id_zona_geografica,
        bairro: (r) => (Number(r.id_municipio) === ID_MANAUS ? r.nm_bairro : null),
        zona: (r) => (r.zona === null || r.zona === undefined || r.zona === "" ? null : Number(r.zona)),
        zonaBairro: (r) =>
          !r.nm_bairro || r.zona === null || r.zona === undefined || r.zona === ""
            ? null
            : Number(r.zona) + "|" + r.nm_bairro,
      };
      const mapas = { pontuacao: {}, votos: {} };
      Object.keys(chaves).forEach((nivel) => {
        mapas.pontuacao[nivel] = this.somarPor(this.pessoas, chaves[nivel], "pontuacao");
        mapas.votos[nivel] = this.somarPor(this.votos, chaves[nivel], "votos");
      });
      return mapas;
    },
    totalVotos() {
      return this.votos.reduce((acc, v) => acc + Number(v.votos), 0);
    },
    totalPontuacao() {
      return this.pessoas.reduce((acc, p) => acc + Number(p.pontuacao || 0), 0);
    },
    kpisResultado() {
      const votosSemCadastro = this.votos.reduce((acc, v) => acc + Number(v.votos_sem_cadastro), 0);
      // So secoes que existem na base do TSE: cadastro em secao inexistente nunca tem voto.
      const cadSemVoto = this.votos
        .filter((v) => v.id_municipio !== null && v.id_municipio !== undefined)
        .reduce((acc, v) => acc + Number(v.cad_sem_voto), 0);
      const votosZonaSemAssessor = this.coberturaZonas
        .filter((z) => z.total === 0)
        .reduce((acc, z) => acc + z.votos, 0);
      const aproveitamento = this.totalPessoas ? this.totalPontuacao / this.totalPessoas : 0;
      return [
        {
          titulo: "Votos da equipe",
          dica: DESCRICOES.votos,
          valor: this.formatNumber(this.totalVotos),
          legenda: this.totalPessoas ? `conversão ${this.formatPct(this.totalVotos / this.totalPessoas)}` : "",
          cor: "primary--text",
        },
        {
          titulo: "Pontuação",
          dica: DESCRICOES.pontuacao,
          valor: this.formatDecimal(this.totalPontuacao),
          legenda: "soma dos fatores dos cadastros",
        },
        {
          titulo: "Aproveitamento",
          dica: DESCRICOES.aproveitamento,
          valor: this.formatPct(aproveitamento),
          legenda: "pontuação ÷ cadastros",
          cor: this.corAproveitamento(aproveitamento) + "--text",
        },
        {
          titulo: "Votos sem cadastro",
          dica: DESCRICOES.votosSemCadastro,
          valor: this.formatNumber(votosSemCadastro),
          legenda: "seções com voto e nenhum cadastro",
        },
        {
          titulo: "Cadastros sem voto",
          dica: DESCRICOES.cadSemVoto,
          valor: this.formatNumber(cadSemVoto),
          legenda: "cadastros em seção sem nenhum voto",
        },
        {
          titulo: "Votos em zona sem assessor",
          dica: DESCRICOES.votosZonaSemAssessor,
          valor: this.formatNumber(votosZonaSemAssessor),
          legenda: "voto conquistado sem cobertura",
        },
      ];
    },
    // ---- Quadrante cadastro x voto ----
    quadrante() {
      const base = this.quadranteNivel === "zona" ? this.coberturaZonas : this.coberturaMunicipios;
      const pontos = base
        .filter((a) => a.eleitorado > 0 && (a.pessoas > 0 || a.votos > 0))
        .map((a) => ({
          ...a,
          x: Number(a.penetracao.toFixed(3)),
          y: Number(a.participacao.toFixed(3)),
        }));
      const mx = this.mediana(pontos.map((p) => p.x));
      const my = this.mediana(pontos.map((p) => p.y));
      pontos.forEach((p) => {
        if (p.x >= mx) p.grupo = p.y >= my ? "forte" : "inflado";
        else p.grupo = p.y >= my ? "organico" : "fraco";
      });
      return { pontos, mx, my };
    },
    gruposQuadrante() {
      const defs = [
        { chave: "organico", titulo: "Voto orgânico — oportunidade", cor: "#1E88E5",
          descricao: "Pouco cadastro e muito voto: o eleitor já vota, falta presença da equipe.",
          ordem: (a, b) => b.votos - a.votos },
        { chave: "inflado", titulo: "Cadastro inflado — rever", cor: "#E53935",
          descricao: "Muito cadastro e pouco voto: cadastro que não se converteu.",
          ordem: (a, b) => b.pessoas - a.pessoas },
        { chave: "forte", titulo: "Forte", cor: "#43A047",
          descricao: "Muito cadastro e muito voto.",
          ordem: (a, b) => b.votos - a.votos },
        { chave: "fraco", titulo: "Fraco", cor: "#9E9E9E",
          descricao: "Pouco cadastro e pouco voto.",
          ordem: (a, b) => b.eleitorado - a.eleitorado },
      ];
      return defs.map((d) => {
        const pontos = this.quadrante.pontos.filter((p) => p.grupo === d.chave);
        return { ...d, pontos, destaques: [...pontos].sort(d.ordem).slice(0, 5) };
      });
    },
    seriesQuadrante() {
      return this.gruposQuadrante.map((g) => ({
        name: g.titulo,
        data: g.pontos.map((p) => ({ x: p.x, y: p.y })),
      }));
    },
    opcoesQuadrante() {
      const self = this;
      const { mx, my } = this.quadrante;
      const linha = (texto) => ({
        borderColor: "#90A4AE",
        strokeDashArray: 6,
        label: { text: texto, style: { color: "#607D8B", background: "transparent" } },
      });
      return {
        chart: {
          toolbar: { show: false },
          zoom: { enabled: false },
          events: {
            dataPointSelection(e, ctx, cfg) {
              const g = self.gruposQuadrante[cfg.seriesIndex];
              const p = g && g.pontos[cfg.dataPointIndex];
              if (p) self.abrirPontoQuadrante(p);
            },
          },
        },
        colors: this.gruposQuadrante.map((g) => g.cor),
        markers: { size: 7, strokeWidth: 1, strokeColors: "#fff" },
        legend: { position: "bottom" },
        xaxis: {
          type: "numeric",
          title: { text: "Penetração do cadastro (% do eleitorado)" },
          labels: { formatter: (v) => Number(v).toFixed(1) + "%" },
          tickAmount: 8,
        },
        yaxis: {
          title: { text: "Participação no voto (% do eleitorado)" },
          labels: { formatter: (v) => Number(v).toFixed(2) + "%" },
        },
        annotations: {
          xaxis: [{ x: mx, ...linha("mediana cadastro") }],
          yaxis: [{ y: my, ...linha("mediana voto") }],
        },
        tooltip: {
          custom({ seriesIndex, dataPointIndex }) {
            const g = self.gruposQuadrante[seriesIndex];
            const p = g && g.pontos[dataPointIndex];
            if (!p) return "";
            const aprov = p.aproveitamento !== null ? self.formatPct(p.aproveitamento) : "—";
            return (
              '<div class="pa-2 caption">' +
              "<strong>" + p.nome + "</strong>" +
              (p.municipiosNomes ? "<br>" + p.municipiosNomes : "") +
              "<br>Cadastros: " + self.formatNumber(p.pessoas) +
              " · Votos: " + self.formatNumber(p.votos) +
              "<br>Eleitorado: " + self.formatNumber(p.eleitorado) +
              "<br>Aproveitamento: " + aprov +
              "</div>"
            );
          },
        },
        grid: { borderColor: "#eee" },
      };
    },
    municipiosCobertos() {
      return this.coberturaMunicipios.filter((m) => m.total > 0).length;
    },
    municipiosSemAssessor() {
      return this.coberturaMunicipios.filter((m) => m.total === 0).length;
    },
    regioesCobertas() {
      return this.coberturaRegioes.filter((r) => r.total > 0).length;
    },
    regioesSemAssessor() {
      return this.coberturaRegioes.filter((r) => r.total === 0).length;
    },
    regioesComCobertura() {
      return this.regioesCobertas;
    },
    // ---- Cruzamento por zona eleitoral (Fase 2) ----
    pessoasPorZona() {
      const mapa = {};
      this.pessoas.forEach((p) => {
        if (p.zona === null || p.zona === undefined || p.zona === "") return;
        const z = Number(p.zona);
        mapa[z] = (mapa[z] || 0) + Number(p.total);
      });
      return mapa;
    },
    coberturaZonas() {
      return this.zonas
        .map((z) => {
          const munIds = Array.isArray(z.municipios) ? z.municipios : [];
          const assessores = this.assessores.filter((a) => {
            const cobreMunicipio = munIds.some((id) => a.municipios.includes(id));
            const cobreRegiao =
              z.id_zona_geografica && a.zonas_geograficas.includes(z.id_zona_geografica);
            return cobreMunicipio || cobreRegiao;
          });
          const pessoas = this.pessoasPorZona[Number(z.zona)] || 0;
          const eleitorado = Number(z.eleitorado_estimado) || 0;
          return {
            id: z.id,
            zona: z.zona,
            nome: `Zona ${z.zona}`,
            municipiosNomes: this.nomesMunicipios(munIds),
            regiao: z.regiao_nome || null,
            pessoas,
            eleitorado,
            penetracao: eleitorado > 0 ? (pessoas / eleitorado) * 100 : null,
            ...this.resultado("zona", Number(z.zona), pessoas, eleitorado),
            total: assessores.length,
            assessores,
          };
        })
        .sort((a, b) => {
          const aDef = a.total === 0 && a.pessoas > 0 ? 1 : 0;
          const bDef = b.total === 0 && b.pessoas > 0 ? 1 : 0;
          if (aDef !== bDef) return bDef - aDef;
          return b.pessoas - a.pessoas;
        });
    },
    zonasFiltradas() {
      return this.soDeficitZona
        ? this.coberturaZonas.filter((z) => z.total === 0)
        : this.coberturaZonas;
    },
    totalPessoas() {
      return this.pessoas.reduce((acc, p) => acc + Number(p.total), 0);
    },
    pessoasEmZonaSemAssessor() {
      return this.coberturaZonas
        .filter((z) => z.total === 0)
        .reduce((acc, z) => acc + z.pessoas, 0);
    },
    zonasNaoQualificadas() {
      const cadastradas = new Set(this.zonas.map((z) => Number(z.zona)));
      return Object.keys(this.pessoasPorZona)
        .map(Number)
        .filter((z) => !cadastradas.has(z));
    },
    municipiosFiltrados() {
      return this.soDeficitInterior
        ? this.coberturaMunicipios.filter((m) => m.total === 0)
        : this.coberturaMunicipios;
    },
    regioesFiltradas() {
      return this.soDeficitCapital
        ? this.coberturaRegioes.filter((r) => r.total === 0)
        : this.coberturaRegioes;
    },
    // ---- Gráfico interior (barras horizontais, só municípios cobertos) ----
    municipiosCobertosLista() {
      return this.coberturaMunicipios
        .filter((m) => m.total > 0)
        .sort((a, b) => b.total - a.total || a.nome.localeCompare(b.nome));
    },
    seriesGraficoInterior() {
      return [{ name: "Assessores", data: this.municipiosCobertosLista.map((m) => m.total) }];
    },
    alturaGraficoInterior() {
      return Math.max(240, this.municipiosCobertosLista.length * 26 + 80);
    },
    opcoesGraficoInterior() {
      const self = this;
      return {
        chart: {
          toolbar: { show: false },
          events: {
            dataPointSelection(e, ctx, cfg) {
              const item = self.municipiosCobertosLista[cfg.dataPointIndex];
              if (item) self.abrirArea(item);
            },
          },
        },
        plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: "70%" } },
        colors: ["#1E88E5"],
        dataLabels: { enabled: true },
        xaxis: {
          categories: this.municipiosCobertosLista.map((m) => m.nome),
          labels: { formatter: (v) => Math.round(v) },
        },
        tooltip: { y: { formatter: (v) => `${v} assessor(es)` } },
        grid: { borderColor: "#eee" },
      };
    },
    // ---- Gráfico capital (donut das regiões) ----
    seriesGraficoCapital() {
      return this.coberturaRegioes.map((r) => r.total);
    },
    opcoesGraficoCapital() {
      const self = this;
      return {
        labels: this.coberturaRegioes.map((r) => r.nome),
        chart: {
          toolbar: { show: false },
          events: {
            dataPointSelection(e, ctx, cfg) {
              const item = self.coberturaRegioes[cfg.dataPointIndex];
              if (item) self.abrirRegiao(item);
            },
          },
        },
        legend: { position: "bottom", horizontalAlign: "left" },
        dataLabels: { enabled: true, formatter: (val, opts) => opts.w.globals.series[opts.seriesIndex] },
        tooltip: { y: { formatter: (v) => `${v} assessor(es)` } },
        theme: { palette: "palette4" },
      };
    },
  },
  created() {
    this.Helpers.verificarToken();
    this.ehAdmin = parseInt(localStorage.getItem("controle"), 10) <= 2;
    const equipe = localStorage.getItem("equipe");
    this.select_equipe = equipe && equipe !== "null" ? parseInt(equipe, 10) : "null";
    if (this.ehAdmin) {
      // O admin escolhe a equipe na tela. Se não houver uma no localStorage,
      // buscarCandidatos() seleciona a primeira e carrega os dados.
      this.buscarCandidatos();
      if (!this.semCandidato) {
        this.carregarDados();
      }
    } else {
      // Não-admin sempre tem a equipe do candidato ativo.
      this.carregarDados();
    }
  },
  methods: {
    formatNumber(value) {
      return (Number(value) || 0).toLocaleString("pt-BR");
    },
    comDica(headers) {
      return headers.filter((h) => h.dica);
    },
    dicaFixa(titulo) {
      return DICAS_KPI[titulo] || "";
    },
    formatDecimal(value) {
      return (Number(value) || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatPct(ratio) {
      return ((Number(ratio) || 0) * 100).toLocaleString("pt-BR", { maximumFractionDigits: 1 }) + "%";
    },
    somarPor(lista, chaveFn, campo) {
      const mapa = {};
      lista.forEach((r) => {
        const k = chaveFn(r);
        if (k === null || k === undefined || k === "") return;
        mapa[k] = (mapa[k] || 0) + (Number(r[campo]) || 0);
      });
      return mapa;
    },
    // Resultado da apuração de uma área: conversão = votos ÷ cadastros (pode passar
    // de 100%); aproveitamento = pontuação ÷ cadastros (0–100%); participação =
    // votos ÷ eleitorado (%, mesma escala da penetração).
    resultado(nivel, chave, pessoas, eleitorado) {
      const votos = this.mapasResultado.votos[nivel][chave] || 0;
      const pontuacao = this.mapasResultado.pontuacao[nivel][chave] || 0;
      return {
        votos,
        pontuacao,
        conversao: pessoas > 0 ? votos / pessoas : null,
        aproveitamento: pessoas > 0 ? pontuacao / pessoas : null,
        participacao: eleitorado > 0 ? (votos / eleitorado) * 100 : null,
      };
    },
    comResultado(headers) {
      if (!this.temApuracao) return headers;
      const extras = [
        { text: "Votos", value: "votos", align: "center", width: 90, dica: DESCRICOES.votos },
        { text: "Conversão", value: "conversao", align: "center", width: 100, dica: DESCRICOES.conversao },
        { text: "Aproveitamento", value: "aproveitamento", align: "center", width: 120, dica: DESCRICOES.aproveitamento },
      ];
      const i = headers.findIndex((h) => h.value === "penetracao");
      return [...headers.slice(0, i + 1), ...extras, ...headers.slice(i + 1)];
    },
    corAproveitamento(ratio) {
      if (ratio === null || ratio === undefined) return "grey";
      if (ratio >= 0.5) return "success";
      if (ratio >= 0.3) return "light-green darken-1";
      if (ratio >= 0.15) return "amber darken-2";
      if (ratio > 0) return "orange darken-3";
      return "grey";
    },
    aproveitamentoAssessor(a) {
      return a.realizado > 0 ? (a.pontuacao || 0) / a.realizado : null;
    },
    // Com apuração, os mais eficientes (pontuação) primeiro; sem, o realizado.
    ordenarAssessores(lista) {
      const campo = this.temApuracao ? "pontuacao" : "realizado";
      return [...(lista || [])].sort((a, b) => (b[campo] || 0) - (a[campo] || 0));
    },
    mediana(valores) {
      if (!valores.length) return 0;
      const v = [...valores].sort((a, b) => a - b);
      const m = Math.floor(v.length / 2);
      return v.length % 2 ? v[m] : (v[m - 1] + v[m]) / 2;
    },
    abrirPontoQuadrante(p) {
      if (this.quadranteNivel === "zona") this.abrirZona(p);
      else this.abrirArea(p);
    },
    nomesMunicipios(ids) {
      return (ids || [])
        .map((id) => {
          const m = this.municipios.find((x) => x.id === id);
          return m ? m.nome : id;
        })
        .join(", ");
    },
    corCobertura(total) {
      if (total >= 3) return "success";
      if (total === 2) return "teal";
      return "amber darken-2";
    },
    corMeta(pct) {
      if (pct === null || pct === undefined) return "grey";
      if (pct >= 100) return "success";
      if (pct >= 80) return "info";
      if (pct >= 50) return "warning";
      return "error";
    },
    atuacaoLabel(tipo) {
      return { capital: "Capital", interior: "Interior", ambos: "Capital e Interior" }[tipo] || "Sem atuação definida";
    },
    iniciais(nome) {
      if (!nome) return "?";
      const partes = String(nome).trim().split(/\s+/);
      const a = partes[0] ? partes[0][0] : "";
      const b = partes.length > 1 ? partes[partes.length - 1][0] : "";
      return (a + b).toUpperCase();
    },
    abrirArea(item) {
      const assessores = this.ordenarAssessores(item.assessores);
      this.areaSelecionada = { nome: item.nome, assessores };
      this.dialogArea = true;
    },
    // Drill-down por região da capital: bairros com pessoas/eleitorado + assessores.
    abrirRegiao(item) {
      const bairros = this.bairros
        .filter((b) => b.id_zona_geografica === item.id)
        .map((b) => {
          const pessoas = this.pessoasPorBairro[b.nm_bairro] || 0;
          const eleitorado = Number(b.eleitorado) || 0;
          return {
            nome: b.nm_bairro,
            pessoas,
            eleitorado,
            penetracao: eleitorado > 0 ? (pessoas / eleitorado) * 100 : null,
            ...this.resultado("bairro", b.nm_bairro, pessoas, eleitorado),
          };
        })
        .sort((a, b) => b.pessoas - a.pessoas || b.eleitorado - a.eleitorado);
      this.regiaoSelecionada = {
        titulo: "Região " + item.nome,
        nome: item.nome,
        pessoas: item.pessoas || 0,
        eleitorado: item.eleitorado || 0,
        penetracao: item.penetracao !== undefined ? item.penetracao : null,
        votos: item.votos || 0,
        conversao: item.conversao !== undefined ? item.conversao : null,
        aproveitamento: item.aproveitamento !== undefined ? item.aproveitamento : null,
        bairros,
        assessores: this.ordenarAssessores(item.assessores),
      };
      this.dialogRegiao = true;
    },
    // Drill-down por bairro da zona eleitoral (reusa o modal de região).
    abrirZona(item) {
      const bairros = this.zonaBairros
        .filter((b) => Number(b.nr_zona) === Number(item.zona))
        .map((b) => {
          const chave = Number(item.zona) + "|" + b.nm_bairro;
          const pessoas = this.pessoasPorZonaBairro[chave] || 0;
          const eleitorado = Number(b.eleitorado) || 0;
          return {
            nome: b.nm_bairro,
            pessoas,
            eleitorado,
            penetracao: eleitorado > 0 ? (pessoas / eleitorado) * 100 : null,
            ...this.resultado("zonaBairro", chave, pessoas, eleitorado),
          };
        })
        .sort((a, b) => b.pessoas - a.pessoas || b.eleitorado - a.eleitorado);
      this.regiaoSelecionada = {
        titulo: "Zona " + item.zona,
        nome: String(item.zona),
        pessoas: item.pessoas || 0,
        eleitorado: item.eleitorado || 0,
        penetracao: item.penetracao !== undefined ? item.penetracao : null,
        votos: item.votos || 0,
        conversao: item.conversao !== undefined ? item.conversao : null,
        aproveitamento: item.aproveitamento !== undefined ? item.aproveitamento : null,
        bairros,
        assessores: this.ordenarAssessores(item.assessores),
      };
      this.dialogRegiao = true;
    },
    buscarCandidatos() {
      const headers = this.Helpers.getHeaderRequisicao();
      AclAPI.getCandidatos(headers)
        .then((response) => {
          if (response.data.token) localStorage.setItem("token", response.data.token);
          if (response.status === 200) this.candidatos = response.data.candidatos || [];
          // Sem equipe definida: seleciona a primeira e carrega (não existe "TODAS").
          if (this.semCandidato && this.candidatos.length) {
            this.select_equipe = this.candidatos[0].idcandidato;
            this.carregarDados();
          }
        })
        .catch(() => this.Helpers.logout());
    },
    carregarDados() {
      // A tela exige uma equipe específica (sem modo "TODAS", que agregaria
      // cadastros compartilhados de forma inconsistente). Sem equipe, não busca.
      if (this.semCandidato) {
        this.assessores = [];
        this.pessoas = [];
        this.votos = [];
        this.temApuracao = false;
        this.zonas = [];
        this.bairros = [];
        this.zonaBairros = [];
        this.loading = false;
        return;
      }
      this.loading = true;
      const headers = this.Helpers.getHeaderRequisicao();
      const params = { equipe: this.select_equipe, assessor: 1 };

      Promise.all([
        UsuarioAPI.getUsuarios(headers, params),
        AclAPI.getMunicipios(headers),
        AclAPI.getZonasGeograficas(headers),
        AclAPI.getZonas(headers),
        AclAPI.getPessoasCobertura(headers, { equipe: this.select_equipe }),
        AclAPI.getBairros(headers),
        DashboardAPI.getAssessores(headers, { equipe: this.select_equipe }),
        AclAPI.getZonaBairros(headers),
      ])
        .then(([usuariosResp, municipiosResp, zonasGeoResp, zonasResp, pessoasResp, bairrosResp, metasResp, zonaBairrosResp]) => {
          const token = usuariosResp.data.token;
          if (token) localStorage.setItem("token", token);

          // Mapa id_assessor → { meta, realizado, percentual } (do vínculo com a equipe)
          const metasMap = {};
          (metasResp.data.assessores || []).forEach((m) => {
            metasMap[m.id] = {
              meta: Number(m.total_geral_meta) || 0,
              realizado: Number(m.total_cadastrado) || 0,
              percentual: m.percentual !== null ? Number(m.percentual) : null,
              pontuacao: Number(m.pontuacao_assessor) || 0,
            };
          });

          this.assessores = (usuariosResp.data.usuarios || []).map((u) => ({
            ...u,
            municipios: Array.isArray(u.municipios) ? u.municipios : [],
            zonas_geograficas: Array.isArray(u.zonas_geograficas) ? u.zonas_geograficas : [],
            meta: metasMap[u.id] ? metasMap[u.id].meta : 0,
            realizado: metasMap[u.id] ? metasMap[u.id].realizado : 0,
            percentual: metasMap[u.id] ? metasMap[u.id].percentual : null,
            pontuacao: metasMap[u.id] ? metasMap[u.id].pontuacao : 0,
          }));
          this.municipios = municipiosResp.data.municipios || [];
          this.zonasGeograficas = zonasGeoResp.data.zonas_geograficas || [];
          this.zonas = (zonasResp.data.zonas || []).map((z) => ({
            ...z,
            municipios: Array.isArray(z.municipios) ? z.municipios : [],
          }));
          this.pessoas = pessoasResp.data.pessoas_agregadas || [];
          this.votos = pessoasResp.data.votos_agregados || [];
          this.temApuracao = !!pessoasResp.data.tem_apuracao;
          // A aba do quadrante só existe com apuração: não deixar a aba ativa órfã.
          if (!this.temApuracao && this.tab > 2) this.tab = 0;
          this.bairros = bairrosResp.data.bairros || [];
          this.zonaBairros = zonaBairrosResp.data.zona_bairros || [];
        })
        .catch((error) => {
          // Só sessão inválida derruba o usuário; erro de servidor fica na tela.
          if (error.response && error.response.status === 401) {
            this.Helpers.logout();
            return;
          }
          this.Helpers.exibirMensagem("Erro ao carregar a análise de cobertura. Tente novamente.", "error", 6000);
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.analise-page {
  background-color: #f5f7fb;
  min-height: 100vh;
}

.kpi-card {
  border-radius: 16px;
  border-color: rgba(0, 0, 0, 0.08);
  transition: box-shadow 0.2s;
}

.kpi-card:hover {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.table-card {
  border-radius: 16px;
  border-color: rgba(0, 0, 0, 0.08);
}

.dialog-card {
  border-radius: 16px;
}

.modern-table {
  border-radius: 0 0 16px 16px;
}

.btn-novo {
  border-radius: 12px;
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0;
}

.styled-select >>> .v-input__slot,
.styled-input >>> .v-input__slot {
  border-radius: 12px !important;
}

.clickable-rows >>> tbody tr {
  cursor: pointer;
}

.clickable-row {
  cursor: pointer;
}
.clickable-rows >>> tbody tr:hover {
  background-color: #f5f5f5 !important;
}
</style>
